<script setup lang="ts" generic="T">
import {
  type IVirtualizedListExpose,
  VIRTUALIZED_LIST_SCROLL_CONTAINER_CLASS,
} from "#/ui-kit/components/ui/virtualized-list/virtualized-list.types";
import { useScroll } from "@vueuse/core";
import { computed, triggerRef, useTemplateRef } from "vue";

export interface IVirtualizedListProps<T> {
  items: T[];
  /**
   * Height of container in px
   */
  viewportHeight?: number;
  /**
   * Height of one item in px
   */
  itemHeight?: number;
}

const props = withDefaults(defineProps<IVirtualizedListProps<T>>(), {
  viewportHeight: 800,
  itemHeight: 40,
});

const slots = defineSlots<{
  item: (props: { index: number; item: T }) => unknown;
}>();

const virtualisedListContainer = useTemplateRef("virtualisedListContainer");
const { y: scrollTop } = useScroll(virtualisedListContainer, { behavior: "smooth" });

const computedItems = computed({
  get: () => props.items,
  set: (val) => (computedItems.value = val),
});

const firstIndexInView = computed<number>(() => {
  return Math.floor(scrollTop.value / props.itemHeight);
});

const lastIndexInView = computed<number>(() => {
  return Math.min(
    firstIndexInView.value + Math.ceil(props.viewportHeight / props.itemHeight),
    computedItems.value.length,
  );
});

const visibleItems = computed<T[]>(() => {
  return computedItems.value.slice(firstIndexInView.value, lastIndexInView.value + 1);
});

function scrollToNode(findItemFn: (item: T) => boolean) {
  const itemIndex = computedItems.value.findIndex(findItemFn);
  if (itemIndex !== -1) scrollToIndex(itemIndex);
}

function scrollToIndex(index: number) {
  const itemTop = props.itemHeight * index;
  const itemCenterOffset = (props.viewportHeight - props.itemHeight) / 2;
  scrollTop.value = itemTop - itemCenterOffset;
}

function refreshView(): void {
  triggerRef(computedItems);
}

defineExpose<IVirtualizedListExpose<T>>({
  refreshView,
  scrollToNode,
  scrollToIndex,
});
</script>

<template>
  <div
    ref="virtualisedListContainer"
    :class="[VIRTUALIZED_LIST_SCROLL_CONTAINER_CLASS, 'overflow-auto']"
    :style="{
      height: `${props.viewportHeight}px`,
    }"
  >
    <div
      :style="{
        height: `${props.itemHeight * computedItems.length}px`,
      }"
    >
      <div
        :style="{
          position: 'relative',
          height: `${visibleItems.length * itemHeight}px`,
          top: `${firstIndexInView * itemHeight}px`,
        }"
      >
        <div
          class="flex items-center h-full w-full"
          v-for="(item, index) of visibleItems"
          :key="index"
          :style="{
            height: `${props.itemHeight}px`,
          }"
        >
          <slot
            :index="index"
            :item="item"
            name="item"
          >
            {{ item }}
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>
